import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import styles from "./TaskEditorSheet.module.css";
import CreatorFragment, {
  CreatorFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import Section from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Section/Section";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import Header from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/Header/Header";
import {
  PatchScheduleTask,
  PatchTask,
} from "@coworker/apprestructured/src/task-management/types/Task";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { NotFound } from "@coworker/apprestructured/src/task-management/components/NotFound/NotFound";
import { useTranslation } from "@coworker/locales";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import { AddImages } from "@coworker/apprestructured/src/task-management/components/AddImages/AddImages";
import AttachFiles from "@coworker/apprestructured/src/task-management/components/AttachFiles/AttachFiles";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import { useTaskFiles } from "@coworker/apprestructured/src/task-management/services/useTaskFiles";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { useQueryClient } from "@tanstack/react-query";
import {
  isNewUpload,
  NewUpload,
} from "@coworker/apprestructured/src/task-management/types/Upload";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import SomethingWentWrongPrompt from "../../SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import ScheduleSaveDecider, {
  ScheduleSaveDecision,
} from "@coworker/apprestructured/src/task-management/components/ScheduleSaveDeciderModal/ScheduleSaveDecider";
import { useUpdatePendingTasksInSchedule } from "@coworker/apprestructured/src/task-management/services/useUpdatePendingTasksInSchedule";
import EditTaskSchedule from "@coworker/apprestructured/src/task-management/components/ScheduleTask/EditTaskSchedule/EditTaskSchedule";
import { useTaskSchedule } from "../../../services/useTaskSchedule";
import { TaskSheetHeader } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskSheetHeader/TaskSheetHeader";
import { TaskCreatorAndEditorSheetFooter } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetFooter/TaskCreatorAndEditorSheetFooter";

const NO_IMAGES: NewOrExistingFile[] = [];
const NO_FILES: NewOrExistingFile[] = [];

/**
 * The TaskEditorSheet is open for the task with id 'taskEditor' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskEditor'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskEditorSheet() {
  const [{ taskEditor: taskId }, setSearchParams] = useSearchParamState();
  // Includes most unsaved changes the user has made to the task.
  const [taskChanges, setTaskChanges] = useState<Record<string, unknown>>({});
  // Images and files are handled separately because the data set when the user
  // makes changes is not the same as we send to the backend, so it cannot
  // be easily merged with the original task.
  // Is only undefined when the user has made no changes.
  const [patchTaskImages, setPatchTaskImages] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [patchTaskFiles, setPatchTaskFiles] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [scheduleSaveDeciderVisible, setScheduleSaveDeciderVisible] =
    useState(false);
  const [scheduleSaveDeciderDecision, setScheduleSaveDeciderDecision] =
    useState<ScheduleSaveDecision>("this");
  const { t } = useTranslation();

  const { mutateAsync: updateTask, error: updateTaskError } = useUpdateTask();
  const {
    mutateAsync: updatePendingTasksInSchedule,
    error: updatePendingTasksInScheduleError,
  } = useUpdatePendingTasksInSchedule();
  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: originalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);
  const { data: originalImages } = useTaskImages(
    taskId,
    originalTask?.image_ids
  );
  const { data: originalFiles } = useTaskFiles(taskId, originalTask?.file_ids);

  const { data: schedule } = useTaskSchedule(taskId);

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    newOrExistingUploads: newOrExistingImageUploads,
    error: imageUploaderError,
  } = useImageUploader(patchTaskImages ?? NO_IMAGES);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    newOrExistingUploads: newOrExistingFileUploads,
    error: fileUploaderError,
  } = useFileUploader(patchTaskFiles ?? NO_FILES);

  // This is the body sent to the backend when saving and includes all changes.
  const patchTask = useMemo<PatchTask>(() => {
    const patchTask: PatchTask = { ...taskChanges };
    if (patchTaskImages !== undefined) {
      patchTask.images = newOrExistingImageUploads;
    }
    if (patchTaskFiles !== undefined) {
      patchTask.files = newOrExistingFileUploads;
    }
    return patchTask;
  }, [
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    patchTaskFiles,
    patchTaskImages,
    taskChanges,
  ]);

  // Clear all changes when the task id changes in the url.
  const handleTaskIdChange = useEffectEvent(() => {
    setTaskChanges({});
    setPatchTaskImages(undefined);
    setPatchTaskFiles(undefined);
    setIsSaving(false);
  });

  useEffect(() => {
    handleTaskIdChange();
  }, [taskId]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return undefined;
    }
    return allTaskTypes.find(({ id }) => id === originalTask.task_type_id);
  }, [originalTask, allTaskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const handleScheduleSaveDeciderClose = useCallback(() => {
    setScheduleSaveDeciderVisible(false);
  }, []);

  const openScheduleSaveDecider = useCallback(() => {
    setScheduleSaveDeciderDecision("this");
    setScheduleSaveDeciderVisible(true);
  }, []);

  const queryClient = useQueryClient();

  const saveThisTask = useCallback(() => {
    setIsSaving(true);

    async function fn() {
      const [updateResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          updateTask({
            id: taskId!,
            patchTask,
          }),
          uploadImages(),
          uploadFiles(),
        ]);
      handleImageUploadResult(
        imageUploadResult,
        newOrExistingImageUploads.filter(isNewUpload)
      );
      handleFileUploadResult(
        fileUploadResult,
        newOrExistingFileUploads.filter(isNewUpload)
      );
      if (updateResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", String(updateResult.value["id"]));
        });
      }
    }

    fn();
  }, [
    disableSaveGuard,
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    patchTask,
    queryClient,
    setSearchParams,
    taskId,
    updateTask,
    uploadFiles,
    uploadImages,
  ]);

  const savePendingTasksInSchedule = useCallback(() => {
    setIsSaving(true);
    const patchScheduleTask = patchTaskToPatchScheduleTask(patchTask);

    async function fn() {
      const [updateResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          updatePendingTasksInSchedule({
            scheduleId: originalTask!.schedule_id!,
            patchScheduleTask,
          }),
          uploadImages(),
          uploadFiles(),
        ]);
      handleImageUploadResult(
        imageUploadResult,
        newOrExistingImageUploads.filter(isNewUpload)
      );
      handleFileUploadResult(
        fileUploadResult,
        newOrExistingFileUploads.filter(isNewUpload)
      );
      if (updateResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", taskId!);
        });
      }
    }

    fn();
  }, [
    disableSaveGuard,
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    originalTask,
    patchTask,
    queryClient,
    setSearchParams,
    taskId,
    updatePendingTasksInSchedule,
    uploadFiles,
    uploadImages,
  ]);

  const handleSaveDeciderConfirmClick = useCallback(() => {
    switch (scheduleSaveDeciderDecision) {
      case "this": {
        saveThisTask();
        break;
      }
      case "all": {
        savePendingTasksInSchedule();
        break;
      }
    }
    setScheduleSaveDeciderVisible(false);
  }, [savePendingTasksInSchedule, saveThisTask, scheduleSaveDeciderDecision]);

  const handleSaveClick = useCallback(() => {
    if (Object.keys(patchTask).length === 0) {
      // We can skip saving if no changes are made
      setSearchParams((prev) => {
        prev.delete("taskEditor");
        prev.set("taskViewer", taskId!);
      });
    } else if (
      originalTask!.schedule_id === null ||
      originalTask!.state !== "pending" ||
      // We never want to bulk patch the start date
      Object.keys(patchTask).includes("start")
    ) {
      saveThisTask();
    } else {
      openScheduleSaveDecider();
    }
  }, [
    openScheduleSaveDecider,
    originalTask,
    patchTask,
    saveThisTask,
    setSearchParams,
    taskId,
  ]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskEditor");
    });
  }, [setSearchParams]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      setTaskChanges((prev) => merge(prev, part));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const blockInput = isSaving || isFetchingOriginalTask || !taskId;

  const mergedTask = useMemo(() => {
    return originalTask ? merge(originalTask, patchTask) : LOADING;
  }, [originalTask, patchTask]);

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput: blockInput,
      task: mergedTask,
      onTaskChange: handleTaskChange,
      showValidation: false,
      taskType: taskType ? taskType : LOADING,
    };
  }, [blockInput, mergedTask, handleTaskChange, taskType]);

  const handleScheduleChange = useCallback(
    (start: string | null) => {
      handleTaskChange({ start });
    },
    [handleTaskChange]
  );

  const start = useMemo(() => {
    if (isLoading(mergedTask) || mergedTask["start"] === null) {
      return null;
    } else {
      return mergedTask["start"] as string;
    }
  }, [mergedTask]);

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      enableSaveGuard();
      setPatchTaskImages(newImages);
    },
    [enableSaveGuard]
  );

  const handleFilesChange = useCallback(
    (newFiles: NewOrExistingFile[]) => {
      enableSaveGuard();
      setPatchTaskFiles(newFiles);
    },
    [enableSaveGuard]
  );

  const images = patchTaskImages ?? originalImages ?? NO_IMAGES;
  const files = patchTaskFiles ?? originalFiles ?? NO_FILES;

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const content = useMemo(() => {
    if (loadingFailed) {
      return (
        <NotFound
          title={t("sorryTaskCouldNotBeFoundString")}
          message={t("thisTaskDoesNotExistString")}
        />
      );
    } else {
      return (
        <div className={styles["sheetsContent"]}>
          <Section>
            <Header number="1" label={t("taskDetailsString")} />
            <FixaListView id="details">
              {taskType ? (
                taskType.creator_fragments.map((config, i) => {
                  return (
                    <CreatorFragment
                      key={i}
                      config={config}
                      commonProps={creatorFragmentCommonProps}
                    />
                  );
                })
              ) : (
                <LoadingCreatorFragment />
              )}
            </FixaListView>
          </Section>
          <Section>
            <Header number="2" label={t("taskSettingsString")} />
            <FixaListView id="settings">
              <CreatorFragment
                config={{ id: "assign-to-creator-fragment", props: {} }}
                commonProps={creatorFragmentCommonProps}
              />
              <CreatorFragment
                config={{
                  id: "switch-creator-fragment",
                  props: {
                    field: "priority",
                    label: t("priorityString"),
                    icon: "notice-circle",
                  },
                }}
                commonProps={creatorFragmentCommonProps}
              />
              <EditTaskSchedule
                disabled={blockInput}
                start={start}
                schedule={schedule}
                onConfirmClick={handleScheduleChange}
              />
              <AddImages
                images={images}
                onConfirmClick={handleImagesChange}
                disabled={blockInput}
              />
              <AttachFiles
                disabled={blockInput}
                files={files}
                onFilesChanged={handleFilesChange}
              />
            </FixaListView>
          </Section>
        </div>
      );
    }
  }, [
    loadingFailed,
    t,
    taskType,
    creatorFragmentCommonProps,
    blockInput,
    start,
    handleScheduleChange,
    images,
    handleImagesChange,
    files,
    handleFilesChange,
    schedule,
  ]);

  const scheduleSaveDecider = useMemo(() => {
    return (
      <ScheduleSaveDecider
        visible={scheduleSaveDeciderVisible}
        decision={scheduleSaveDeciderDecision}
        onConfirmClick={handleSaveDeciderConfirmClick}
        onClose={handleScheduleSaveDeciderClose}
        onDecisionChange={setScheduleSaveDeciderDecision}
      />
    );
  }, [
    handleScheduleSaveDeciderClose,
    handleSaveDeciderConfirmClick,
    scheduleSaveDeciderDecision,
    scheduleSaveDeciderVisible,
  ]);

  const showSomethingWentWrongPrompt: boolean =
    !!updateTaskError ||
    !!updatePendingTasksInScheduleError ||
    !!imageUploaderError ||
    !!fileUploaderError;

  return (
    <>
      <FixaModal visible={!!taskId} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          fullSize
          className={loadingFailed ? "" : styles["greyBackground"]}
          header={
            <TaskSheetHeader
              onClose={handleCloseClick}
              title={loadingFailed ? "" : t("editTaskString")}
            />
          }
          footer={
            loadingFailed ? null : (
              <TaskCreatorAndEditorSheetFooter
                onClick={handleSaveClick}
                text={t("saveTaskString")}
                isLoading={isSaving}
                isDisabled={
                  !taskType ||
                  !originalTask ||
                  !taskId ||
                  !imageUploaderIsReady ||
                  !fileUploaderIsReady
                }
              />
            )
          }
        >
          {content}
        </FixaSheets>
      </FixaModal>
      {scheduleSaveDecider}
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  newImageUploads: NewUpload[]
) {
  if (imageUploadResult.status === "rejected") {
    const bucketIds = newImageUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("Image upload failed", { bucketIds }, "error");
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  newFileUploads: NewUpload[]
) {
  if (fileUploadResult.status === "rejected") {
    const bucketIds = newFileUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("File upload failed", { bucketIds }, "error");
  }
}

// prettier-ignore
function patchTaskToPatchScheduleTask(patchTask: PatchTask): PatchScheduleTask {
  const patchScheduleTask: PatchScheduleTask = {};
  if (patchTask.title !== undefined)
    patchScheduleTask.title = patchTask.title;
  if (patchTask.priority !== undefined)
    patchScheduleTask.priority = patchTask.priority;
  if (patchTask.details !== undefined)
    patchScheduleTask.details = patchTask.details;
  if (patchTask.assigned_user_id !== undefined)
    patchScheduleTask.assigned_user_id = patchTask.assigned_user_id;
  if (patchTask.assigned_team_id !== undefined)
    patchScheduleTask.assigned_team_id = patchTask.assigned_team_id;
  if (patchTask.images !== undefined)
    patchScheduleTask.images = patchTask.images;
  if (patchTask.files !== undefined)
    patchScheduleTask.files = patchTask.files;
  return patchScheduleTask;
}

export default TaskEditorSheet;
